export interface CanonicalTreePostMigrationHistoricalSourcePhysicalDeletionDecision {
  readonly candidate:
    | 'BROWSER_SOURCE'
    | 'REACT_STORE'
    | 'ARCH_035_RUNTIME'
    | 'ARCH_042_PARITY';

  readonly sourcePath:
    string;

  readonly productionImportCount:
    0;

  readonly activeBarrelExportPresent:
    false;

  readonly mountedProductionReferencePresent:
    false;

  readonly immutableFixturePreserved:
    true;

  readonly historicalVerifierFixtureBacked:
    true;

  readonly typescriptDeletionCertified:
    true;

  readonly productionBuildDeletionCertified:
    true;

  readonly governanceDeletionCertified:
    true;

  readonly physicalDeletionAuthorized:
    true;

  readonly physicalDeletionCompleted:
    true;
}

export interface CanonicalTreePostMigrationHistoricalSourcePhysicalDeletionAuthority {
  readonly milestone:
    'CC-TREE-ARCH-066';

  readonly authority:
    'POST_MIGRATION_HISTORICAL_SOURCE_PHYSICAL_DELETION_AUTHORITY';

  readonly decision:
    'FOUR_HISTORICAL_REPOSITORY_SOURCES_PHYSICALLY_DELETED_AFTER_FULL_PORTABILITY_CERTIFICATION';

  readonly candidateCount:
    4;

  readonly deletedCandidateCount:
    4;

  readonly typescriptSimulationPassed:
    true;

  readonly productionBuildSimulationPassed:
    true;

  readonly governanceSimulationFailureCount:
    0;

  readonly allGovernanceGatesDeletionTolerant:
    true;

  readonly activeProductionReferenceCount:
    0;

  readonly allHistoricalTruthArchiveBacked:
    true;

  readonly arch056ArchiveChanged:
    false;

  readonly arch062ArchiveChanged:
    false;

  readonly historicalSourceDeletionAuthorized:
    true;

  readonly historicalSourceDeletionCompleted:
    true;

  readonly historicalVerifierDeletionAuthorized:
    false;

  readonly historicalDocumentationDeletionAuthorized:
    false;

  readonly certifiedFixturesMayBeMutated:
    false;

  readonly registryOnlyProductionAuthorityPreserved:
    true;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly decisions:
    readonly CanonicalTreePostMigrationHistoricalSourcePhysicalDeletionDecision[];

  readonly nextGovernedDependency:
    'POST_DELETION_HISTORICAL_AUTHORITY_CONSOLIDATION';
}

const candidates =
  Object.freeze([
    Object.freeze({
      candidate:
        'BROWSER_SOURCE' as const,

      sourcePath:
        'src/governor/tree/CanonicalTreeExternalEvidenceBrowserSource.ts',
    }),

    Object.freeze({
      candidate:
        'REACT_STORE' as const,

      sourcePath:
        'src/hooks/governor/useCanonicalTreeExternalEvidence.ts',
    }),

    Object.freeze({
      candidate:
        'ARCH_035_RUNTIME' as const,

      sourcePath:
        'src/governor/tree/CanonicalTreeExternalEvidenceWorkspaceRuntimeCertification.ts',
    }),

    Object.freeze({
      candidate:
        'ARCH_042_PARITY' as const,

      sourcePath:
        'src/governor/tree/CanonicalTreeBrowserRegistryParityCertification.ts',
    }),
  ]);

export const resolveCanonicalTreePostMigrationHistoricalSourcePhysicalDeletionAuthority =
  (): CanonicalTreePostMigrationHistoricalSourcePhysicalDeletionAuthority =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-066',

      authority:
        'POST_MIGRATION_HISTORICAL_SOURCE_PHYSICAL_DELETION_AUTHORITY',

      decision:
        'FOUR_HISTORICAL_REPOSITORY_SOURCES_PHYSICALLY_DELETED_AFTER_FULL_PORTABILITY_CERTIFICATION',

      candidateCount:
        4,

      deletedCandidateCount:
        4,

      typescriptSimulationPassed:
        true,

      productionBuildSimulationPassed:
        true,

      governanceSimulationFailureCount:
        0,

      allGovernanceGatesDeletionTolerant:
        true,

      activeProductionReferenceCount:
        0,

      allHistoricalTruthArchiveBacked:
        true,

      arch056ArchiveChanged:
        false,

      arch062ArchiveChanged:
        false,

      historicalSourceDeletionAuthorized:
        true,

      historicalSourceDeletionCompleted:
        true,

      historicalVerifierDeletionAuthorized:
        false,

      historicalDocumentationDeletionAuthorized:
        false,

      certifiedFixturesMayBeMutated:
        false,

      registryOnlyProductionAuthorityPreserved:
        true,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      decisions:
        Object.freeze(
          candidates.map(
            candidate =>
              Object.freeze({
                ...candidate,

                productionImportCount:
                  0 as const,

                activeBarrelExportPresent:
                  false as const,

                mountedProductionReferencePresent:
                  false as const,

                immutableFixturePreserved:
                  true as const,

                historicalVerifierFixtureBacked:
                  true as const,

                typescriptDeletionCertified:
                  true as const,

                productionBuildDeletionCertified:
                  true as const,

                governanceDeletionCertified:
                  true as const,

                physicalDeletionAuthorized:
                  true as const,

                physicalDeletionCompleted:
                  true as const,
              }),
          ),
        ),

      nextGovernedDependency:
        'POST_DELETION_HISTORICAL_AUTHORITY_CONSOLIDATION',
    });
